import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SeoMeta } from '../components/seo/SeoMeta';
import { generateChineseName } from '../lib/api/nameGenerator';
import { NameSuggestion } from '../types/name';
import { NameList } from '../components/name-generator/NameList';
import { ProgressBar } from '../components/ui/ProgressBar';

interface Surname {
  name: string;
  pinyin: string;
  meaning: string;
  popularity: number;
  regions: string[];
}

export function ChineseSurnames() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSurname, setSelectedSurname] = useState<Surname | null>(null);
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !surname.trim()) {
      setError('Both first name and surname are required.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);

    try {
      const response = await generateChineseName(firstName.trim(), surname.trim(), 'any');
      setSuggestions(response.suggestions);
      setSelectedIndex(0);
      
      setTimeout(() => {
        const resultsElement = document.getElementById('name-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error occurred while generating names');
    } finally {
      setIsLoading(false);
    }
  };

  const popularSurnames: Surname[] = [
    {
      name: "李",
      pinyin: "Lǐ",
      meaning: "Originally meant 'plum tree'. The Li clan was one of the most prominent families during the Tang Dynasty (618-907 CE). The surname has a rich history in Chinese culture and is also common in Korea (as 'Lee') and Vietnam.",
      popularity: 1,
      regions: ["Throughout China", "Korea (as 'Lee')", "Vietnam", "Singapore"]
    },
    {
      name: "王",
      pinyin: "Wáng",
      meaning: "Means 'king' or 'monarch'. The Wang clan has a history dating back over 4,000 years to the Zhou Dynasty (1046-256 BCE). It was originally given to descendants of ancient Chinese rulers.",
      popularity: 2,
      regions: ["Northern China", "Taiwan", "Hong Kong", "Malaysia"]
    },
    {
      name: "张",
      pinyin: "Zhāng",
      meaning: "Originally meant 'to draw a bow'. Derived from an ancient officer title during the Zhou Dynasty who was in charge of archery. The Zhang clan has produced many notable military strategists and scholars.",
      popularity: 3,
      regions: ["Central China", "Taiwan", "Southeast Asia"]
    },
    {
      name: "刘",
      pinyin: "Liú",
      meaning: "Associated with 'kill' in ancient times but gained prominence as the imperial surname of the Han Dynasty (206 BCE-220 CE). The Liu clan has significantly influenced Chinese literature and politics.",
      popularity: 4,
      regions: ["Southern China", "Northeast China", "Malaysia", "Singapore"]
    },
    {
      name: "陈",
      pinyin: "Chén",
      meaning: "Originated from the State of Chen in modern-day Henan Province. Represents 'morning' or 'exhibition'. The Chen family has historically been associated with commerce and scholarship.",
      popularity: 5,
      regions: ["Southern China", "Fujian Province", "Taiwan", "Southeast Asia"]
    },
    {
      name: "杨",
      pinyin: "Yáng",
      meaning: "Derived from the word for 'poplar tree'. The Yang clan originated from the ancient state of Yang during the Spring and Autumn Period (771-476 BCE).",
      popularity: 6,
      regions: ["Sichuan Province", "Shaanxi Province", "Southeast Asia"]
    },
    {
      name: "黄",
      pinyin: "Huáng",
      meaning: "Means 'yellow' or 'imperial'. Associated with the Yellow Emperor, a legendary Chinese sovereign. The surname represents nobility and imperial authority.",
      popularity: 7,
      regions: ["Southern China", "Guangdong Province", "Taiwan"]
    },
    {
      name: "赵",
      pinyin: "Zhào",
      meaning: "Originated from the State of Zhao during the Warring States period (475-221 BCE). One of the most prestigious surnames in Chinese history.",
      popularity: 8,
      regions: ["Northern China", "Shanxi Province", "Korea (as 'Cho')"]
    },
    {
      name: "吴",
      pinyin: "Wú",
      meaning: "Derived from the State of Wu during the Spring and Autumn Period. Associated with military prowess and leadership.",
      popularity: 9,
      regions: ["Jiangsu Province", "Zhejiang Province", "Southeast Asia"]
    },
    {
      name: "周",
      pinyin: "Zhōu",
      meaning: "Named after the Zhou Dynasty, one of the most influential dynasties in Chinese history. Represents cultural sophistication and philosophical tradition.",
      popularity: 10,
      regions: ["Central China", "Sichuan Province", "Hong Kong"]
    },
    {
      name: "徐",
      pinyin: "Xú",
      meaning: "Means 'slowly' or 'gently'. The surname originated from the State of Xu during the Spring and Autumn Period. Known for producing many scholars and artists.",
      popularity: 11,
      regions: ["Jiangsu Province", "Shanghai", "Eastern China"]
    },
    {
      name: "孙",
      pinyin: "Sūn",
      meaning: "Originally meant 'grandson'. The Sun clan has a strong connection to military strategy, with Sun Tzu being its most famous member.",
      popularity: 12,
      regions: ["Shandong Province", "Northern China", "Korea (as 'Son')"]
    },
    {
      name: "马",
      pinyin: "Mǎ",
      meaning: "Means 'horse'. Associated with the ancient Silk Road and cavalry traditions. The Ma clan has strong ties to both Chinese and Muslim cultures.",
      popularity: 13,
      regions: ["Northwest China", "Gansu Province", "Malaysia"]
    },
    {
      name: "朱",
      pinyin: "Zhū",
      meaning: "Means 'vermilion red'. Was the imperial surname of the Ming Dynasty (1368-1644 CE). Associated with good fortune and prosperity.",
      popularity: 14,
      regions: ["Jiangsu Province", "Zhejiang Province", "Southeast China"]
    },
    {
      name: "胡",
      pinyin: "Hú",
      meaning: "Originally referred to non-Han Chinese peoples from the north. Now associated with wisdom and cultural exchange.",
      popularity: 15,
      regions: ["Anhui Province", "Central China", "Southeast Asia"]
    },
    {
      name: "郭",
      pinyin: "Guō",
      meaning: "Derived from the word for 'outer city wall'. Associated with protection and defense. Has produced many military strategists.",
      popularity: 16,
      regions: ["Shanxi Province", "Northern China", "Taiwan"]
    },
    {
      name: "何",
      pinyin: "Hé",
      meaning: "Means 'what' or 'why'. Originally from the State of He during the Spring and Autumn Period. Known for philosophical traditions.",
      popularity: 17,
      regions: ["Guangdong Province", "Southern China", "Hong Kong"]
    },
    {
      name: "罗",
      pinyin: "Luó",
      meaning: "Possibly derived from the word for 'net' or 'collect'. Has connections to both Han Chinese and minority ethnic groups.",
      popularity: 18,
      regions: ["Guangxi Province", "Southern China", "Vietnam"]
    },
    {
      name: "高",
      pinyin: "Gāo",
      meaning: "Means 'tall' or 'high'. Associated with nobility and high social status. The surname gained prominence during the Tang Dynasty.",
      popularity: 19,
      regions: ["Shandong Province", "Northern China", "Korea (as 'Ko')"]
    },
    {
      name: "林",
      pinyin: "Lín",
      meaning: "Means 'forest'. Associated with nature and growth. Particularly common in southeastern China and among overseas Chinese.",
      popularity: 20,
      regions: ["Fujian Province", "Taiwan", "Southeast Asia"]
    },
    {
      name: "郑",
      pinyin: "Zhèng",
      meaning: "Named after the State of Zheng during the Spring and Autumn Period. Known for diplomatic and administrative traditions.",
      popularity: 21,
      regions: ["Henan Province", "Fujian Province", "Southeast Asia"]
    },
    {
      name: "梁",
      pinyin: "Liáng",
      meaning: "Means 'bridge' or 'beam'. Associated with the Liang Dynasty (502-557 CE) and cultural achievements in literature and arts.",
      popularity: 22,
      regions: ["Sichuan Province", "Central China", "Malaysia"]
    },
    {
      name: "谢",
      pinyin: "Xiè",
      meaning: "Means 'thank' or 'decline'. The Xie clan has a strong tradition in literature and poetry, particularly during the Six Dynasties period.",
      popularity: 23,
      regions: ["Zhejiang Province", "Southern China", "Taiwan"]
    },
    {
      name: "宋",
      pinyin: "Sòng",
      meaning: "Named after the State of Song. Associated with the Song Dynasty (960-1279 CE), known for cultural and technological advancement.",
      popularity: 24,
      regions: ["Henan Province", "Northern China", "Korea"]
    },
    {
      name: "唐",
      pinyin: "Táng",
      meaning: "Named after the Tang Dynasty (618-907 CE). Represents cultural sophistication and the golden age of Chinese civilization.",
      popularity: 25,
      regions: ["Guangdong Province", "Southern China", "Hong Kong"]
    },
    {
      name: "许",
      pinyin: "Xǔ",
      meaning: "Means 'allow' or 'permit'. Originally from the State of Xu. Known for producing scholars and government officials.",
      popularity: 26,
      regions: ["Henan Province", "Eastern China", "Malaysia"]
    },
    {
      name: "韩",
      pinyin: "Hán",
      meaning: "Named after the State of Han. The surname has strong connections to both Chinese and Korean culture.",
      popularity: 27,
      regions: ["Shanxi Province", "Northern China", "Korea (as 'Han')"]
    },
    {
      name: "冯",
      pinyin: "Féng",
      meaning: "Originally meant 'gallop'. Associated with horsemanship and military prowess in ancient China.",
      popularity: 28,
      regions: ["Hebei Province", "Northern China", "Inner Mongolia"]
    },
    {
      name: "邓",
      pinyin: "Dèng",
      meaning: "Named after the ancient State of Deng. Gained prominence during the late 20th century through political leadership.",
      popularity: 29,
      regions: ["Sichuan Province", "Central China", "Guangdong Province"]
    },
    {
      name: "曹",
      pinyin: "Cáo",
      meaning: "Originally referred to officials who managed horses. Famous for the Cao clan's role in the Three Kingdoms period.",
      popularity: 30,
      regions: ["Anhui Province", "Eastern China", "Southeast Asia"]
    },
    {
      name: "曾",
      pinyin: "Zēng",
      meaning: "Originally meant 'already' or 'once'. The Zeng clan has a strong presence in southern China and is known for educational achievements.",
      popularity: 31,
      regions: ["Guangdong Province", "Fujian Province", "Taiwan"]
    },
    {
      name: "彭",
      pinyin: "Péng",
      meaning: "Named after Mount Peng in ancient China. Associated with strength and resilience in Chinese culture.",
      popularity: 32,
      regions: ["Hunan Province", "Southern China", "Southeast Asia"]
    },
    {
      name: "董",
      pinyin: "Dǒng",
      meaning: "Originally meant 'supervisor' or 'overseer'. The Dong clan has a tradition of leadership in both government and business.",
      popularity: 33,
      regions: ["Henan Province", "Northern China", "Singapore"]
    },
    {
      name: "袁",
      pinyin: "Yuán",
      meaning: "Possibly derived from the word for 'long robe'. The Yuan clan has significant influence in Chinese political history.",
      popularity: 34,
      regions: ["Jiangxi Province", "Central China", "Hong Kong"]
    },
    {
      name: "潘",
      pinyin: "Pān",
      meaning: "Associated with water and fluidity. The Pan clan has strong connections to commerce and maritime trade.",
      popularity: 35,
      regions: ["Zhejiang Province", "Coastal China", "Southeast Asia"]
    },
    {
      name: "蒋",
      pinyin: "Jiǎng",
      meaning: "Originally referred to a type of herb. The Jiang clan gained prominence through political leadership in modern Chinese history.",
      popularity: 36,
      regions: ["Zhejiang Province", "Taiwan", "Southeast China"]
    },
    {
      name: "蔡",
      pinyin: "Cài",
      meaning: "Named after the State of Cai. The Cai clan is known for achievements in literature and education.",
      popularity: 37,
      regions: ["Fujian Province", "Taiwan", "Southeast Asia"]
    },
    {
      name: "余",
      pinyin: "Yú",
      meaning: "Means 'surplus' or 'abundance'. The Yu clan has traditions in both scholarship and commerce.",
      popularity: 38,
      regions: ["Jiangxi Province", "Southern China", "Malaysia"]
    },
    {
      name: "杜",
      pinyin: "Dù",
      meaning: "Means 'birchleaf pear tree'. The Du clan has produced many famous poets and scholars throughout history.",
      popularity: 39,
      regions: ["Henan Province", "Northern China", "Vietnam"]
    },
    {
      name: "叶",
      pinyin: "Yè",
      meaning: "Means 'leaf' or 'page'. The Ye clan is associated with literature and education in Chinese culture.",
      popularity: 40,
      regions: ["Zhejiang Province", "Southern China", "Southeast Asia"]
    },
    {
      name: "苏",
      pinyin: "Sū",
      meaning: "Originally meant 'to revive'. The Su clan is renowned for literary achievements, particularly in poetry and prose.",
      popularity: 41,
      regions: ["Jiangsu Province", "Eastern China", "Singapore"]
    },
    {
      name: "魏",
      pinyin: "Wèi",
      meaning: "Named after the State of Wei. Associated with the Three Kingdoms period and military strategy.",
      popularity: 42,
      regions: ["Shanxi Province", "Northern China", "Korea"]
    },
    {
      name: "程",
      pinyin: "Chéng",
      meaning: "Means 'journey' or 'procedure'. The Cheng clan has contributed significantly to Chinese philosophy.",
      popularity: 43,
      regions: ["Henan Province", "Central China", "Southeast Asia"]
    },
    {
      name: "莫",
      pinyin: "Mò",
      meaning: "Originally meant 'sunset' or 'must not'. The Mo clan has connections to both Chinese and Vietnamese cultures.",
      popularity: 44,
      regions: ["Guangxi Province", "Southern China", "Vietnam"]
    },
    {
      name: "江",
      pinyin: "Jiāng",
      meaning: "Means 'river'. The Jiang clan has strong associations with regions along the Yangtze River.",
      popularity: 45,
      regions: ["Jiangxi Province", "Southern China", "Taiwan"]
    },
    {
      name: "石",
      pinyin: "Shí",
      meaning: "Means 'stone' or 'rock'. Symbolizes stability and endurance in Chinese culture.",
      popularity: 46,
      regions: ["Hebei Province", "Northern China", "Inner Mongolia"]
    },
    {
      name: "戴",
      pinyin: "Dài",
      meaning: "Means 'to wear' or 'to respect'. The Dai clan has traditions in scholarship and education.",
      popularity: 47,
      regions: ["Anhui Province", "Eastern China", "Malaysia"]
    },
    {
      name: "段",
      pinyin: "Duàn",
      meaning: "Originally meant 'section' or 'segment'. Has historical connections to minority ethnic groups in China.",
      popularity: 48,
      regions: ["Yunnan Province", "Southwest China", "Vietnam"]
    },
    {
      name: "萧",
      pinyin: "Xiāo",
      meaning: "Named after a type of flute. The Xiao clan has strong traditions in arts and music.",
      popularity: 49,
      regions: ["Zhejiang Province", "Southern China", "Southeast Asia"]
    },
    {
      name: "田",
      pinyin: "Tián",
      meaning: "Means 'field' or 'farmland'. The Tian clan has deep connections to agriculture and land management.",
      popularity: 50,
      regions: ["Shandong Province", "Northern China", "Korea"]
    }
  ];

  const filteredSurnames = popularSurnames.filter(surname =>
    surname.pinyin.toLowerCase().includes(searchTerm.toLowerCase()) ||
    surname.meaning.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <SeoMeta
        title="Chinese Last Names - Origins & Meanings | Chinese-Name.net"
        description="Chinese last names with meanings, origins, and pronunciation. Compare common surnames, learn family history, and choose the right surname context."
        pathname="/chinese-last-names"
      />
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm">
            <RouterLink to="/" className="text-gray-500 hover:text-gray-700">Home</RouterLink>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900">Chinese Last Names</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-extrabold text-gray-900 mb-6">Chinese Last Names: 100+ Common Surnames with Meanings</h1>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-4">
              Discover the perfect Chinese surname for your name. This comprehensive guide features over 100 common Chinese last names with origins, meanings, and cultural history.
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Explore the rich heritage and fascinating stories behind Chinese surnames.
            </p>
          </div>

          {/* Quick CTA */}
          <div className="mt-8 bg-purple-50 rounded-lg shadow-md p-6 max-w-2xl mx-auto mb-8">
            <p className="text-center text-gray-700 mb-4">
              💡 Want a personalized Chinese name with a surname?
            </p>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Name with Surname →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>

          {/* Generated Names Results */}
          {suggestions.length > 0 && (
            <div id="name-results" className="bg-gray-50 py-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                  Your Generated Chinese Names
                </h2>
                <NameList
                  suggestions={suggestions}
                  selectedIndex={selectedIndex}
                  onSelect={setSelectedIndex}
                />
              </div>
            </div>
          )}

          {/* Most Common Chinese Surnames Section */}
          <div className="mt-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" id="common">
                Common Chinese Last Names
            </h2>
            <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto">
              Explore the top 50 most popular Chinese last names, their meanings, and regional distributions across China and Asia.
            </p>
          </div>
          </div>
        </div>
      </section>

      {/* Introduce Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Are Chinese Last Names?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Chinese last names (surnames or family names) are an essential part of Chinese identity and culture. With over 5,000 recorded surnames, Chinese last names carry deep historical significance, regional distribution patterns, and cultural meanings. The most common surnames like 王 (Wáng), 李 (Lǐ), and 张 (Zhāng) represent millions of people across China and Chinese communities worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Learn About Chinese Last Names?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Understanding Chinese surnames connects you to rich cultural heritage and meaningful family history.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">📜</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Historical Significance</h3>
              <p className="text-gray-600 text-sm">Chinese surnames reflect thousands of years of Chinese history and cultural evolution.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">🌏</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Regional Patterns</h3>
              <p className="text-gray-600 text-sm">Discover how surnames are distributed across different regions of China and Asia.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Genealogy Research</h3>
              <p className="text-gray-600 text-sm">Learn about your Chinese heritage and trace your family's historical roots.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Chinese surnames and meanings Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese surnames and meanings
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every surname carries a story of place, profession, or virtue. Learn Chinese surname meanings so you understand how each character shaped family legacies.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Chinese last name meanings</h3>
              <p className="text-gray-600 mb-4">
                From 王 meaning “king” to 李 meaning “plum,” we unpack the Chinese last name meanings so you can choose a surname rich with symbolism.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Common Chinese last names</h3>
              <p className="text-gray-600 mb-4">
                The top surnames like Wang, Li, Zhang, and Liu appear across the country. Each entry explains why those names became so widespread.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Chinese surname meanings</h3>
              <p className="text-gray-600 mb-4">
                Track how a surname meaning evolved from occupations, geographical features, or legendary figures centuries ago.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Chinese Last Names Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Search Bar */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search Chinese surnames by pinyin or meaning..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Top 10 Chinese Surnames Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Top 10 Chinese Surnames
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
              {popularSurnames.slice(0, 10).map((surname, index) => (
                <div 
                  key={index}
                  className="text-center p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedSurname(surname)}
                >
                  <div className="text-3xl font-bold text-indigo-600 mb-2">{surname.name}</div>
                  <div className="text-sm text-gray-600">{surname.pinyin}</div>
                  <div className="text-xs text-gray-500">#{surname.popularity}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Chinese Last Names with Meanings Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Chinese Last Names with Meanings
            </h2>
            <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto">
              Each Chinese surname carries deep historical and cultural significance. Click on any surname below to learn its detailed meaning and regional distribution.
            </p>

            {/* Surnames Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredSurnames.map((surname, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer p-6 border-l-4 ${
                    selectedSurname?.name === surname.name ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                  onClick={() => setSelectedSurname(surname)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-gray-900">{surname.name}</span>
                      <span className="text-lg text-indigo-600 font-medium">{surname.pinyin}</span>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      #{surname.popularity}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {surname.meaning}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {surname.regions.slice(0, 2).map((region, regionIndex) => (
                      <span
                        key={regionIndex}
                        className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded"
                      >
                        {region}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Origins and History Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Origins and History of Chinese Surnames
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Chinese Surname Pronunciation Guide</h3>
                <p className="text-gray-600 mb-4">
                  Chinese surnames are typically pronounced with specific tones that are crucial for correct identification. Our guide includes pinyin pronunciation with tone marks to help you pronounce each surname accurately.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Tone marks indicate rising, falling, or level pronunciations</li>
                  <li>• Each surname has historical significance and meaning</li>
                  <li>• Regional variations may affect pronunciation</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Regional Distribution of Chinese Last Names</h3>
                <p className="text-gray-600 mb-4">
                  Chinese surnames show distinct regional patterns across China and Asia. Some surnames are more common in northern regions, while others dominate in southern provinces.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Northern China: Wang, Li, Zhang prevalence</li>
                  <li>• Southern China: Chen, Lin, Huang concentration</li>
                  <li>• Overseas communities maintain traditional patterns</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cultural Significance Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Cultural Significance of Family Names
            </h3>
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Chinese family names represent more than just identification—they carry thousands of years of history, cultural values, and ancestral connections. Understanding the meaning behind Chinese surnames helps appreciate the depth of Chinese cultural heritage.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl mb-2">🏛️</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Historical Legacy</h4>
                  <p className="text-sm text-gray-600">Many surnames trace back to ancient states, dynasties, and legendary figures</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🌍</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Global Presence</h4>
                  <p className="text-sm text-gray-600">Chinese surnames are found worldwide, maintaining cultural connections</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">👨‍👩‍👧‍👦</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Family Identity</h4>
                  <p className="text-sm text-gray-600">Surnames connect individuals to their ancestral roots and family heritage</p>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Surname Details Modal */}
          {selectedSurname && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedSurname.name} ({selectedSurname.pinyin})
                    </h3>
                    <span className="text-lg text-indigo-600">
                      Ranked #{selectedSurname.popularity} most common
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedSurname(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Meaning & Origin</h4>
                    <p className="text-gray-600 leading-relaxed">{selectedSurname.meaning}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Regional Distribution</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedSurname.regions.map((region, index) => (
                        <span
                          key={index}
                          className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
                        >
                          {region}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Chinese Last Names and Their Meanings */}
      <section id="meanings" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese Last Names and Their Meanings
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Discover the fascinating meanings behind Chinese surnames. Each surname carries historical significance and cultural meaning that reflects Chinese history and values.
            </p>
          </div>
        </div>
      </section>

      {/* Rare Chinese Surnames */}
      <section id="rare" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Rare Chinese Surnames
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Explore unique and rare Chinese family names that are less common but carry rich cultural heritage and interesting origins.
            </p>
          </div>
        </div>
      </section>

      {/* History of Chinese Family Names */}
      <section id="history" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              History of Chinese Family Names
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Learn about the origins and evolution of Chinese surnames throughout history, from ancient dynasties to modern times.
            </p>
          </div>
        </div>
      </section>

      {/* Chinese signet rings last name lee crest Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese signet rings last name lee crest
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn how the Lee family crest inspired Chinese signet rings that combine the 李 surname with plum blossoms, strength, and honor in a custom seal.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4 text-gray-600">
            <p>
              The Lee crest merges the ancient 李 surname with plum tree imagery, symbolizing resilience and renewal. Signet rings engraved with this crest honor the Lee lineage in Singapore, Korea, and beyond.
            </p>
            <p>
              Collectors appreciate the detailed work on Chinese signet rings last name lee crest pieces because they blend historical meaning with modern craftsmanship.
            </p>
            <div className="mt-6 bg-purple-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Learn More About Chinese Signet Rings
              </h3>
              <p className="text-gray-700 mb-4">
                Discover the rich heritage of Chinese signet rings featuring family crests, ancient seal designs, and traditional symbolism in our comprehensive guide.
              </p>
              <RouterLink
                to="/blog/chinese-signet-rings"
                className="inline-block bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors font-medium"
              >
                Read Full Article →
              </RouterLink>
            </div>
          </div>
        </div>
      </section>

      {/* Choose the Right Chinese Last Name */}
      <section id="choose" className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Choose the Right Chinese Last Name
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Select a Chinese surname that matches your preferences, cultural background, and personal identity. Use our generator to create a complete Chinese name.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Name with Surname →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Explore names by category</h3>
              <ul className="space-y-3">
                <li><RouterLink to="/chinese-boy-names" className="text-purple-600 hover:underline">Chinese Boy Names with Meanings</RouterLink></li>
                <li><RouterLink to="/chinese-girl-names" className="text-purple-600 hover:underline">Chinese Girl Names by Style and Meaning</RouterLink></li>
                <li><RouterLink to="/cute-chinese-girl-names" className="text-purple-600 hover:underline">Cute Chinese Girl Names for softer name ideas</RouterLink></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Tools and guides</h3>
              <ul className="space-y-3">
                <li><RouterLink to="/chinese-name-generator" className="text-purple-600 hover:underline">Generate a full Chinese name with surname</RouterLink></li>
                <li><RouterLink to="/name-meanings" className="text-purple-600 hover:underline">Understand Chinese character meanings before choosing</RouterLink></li>
                <li><RouterLink to="/pronunciation" className="text-purple-600 hover:underline">Learn surname pronunciation with pinyin guidance</RouterLink></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About Chinese Last Names
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What is the most common Chinese last name?
              </h3>
              <p className="text-gray-600">
                王 (Wáng) is the most common Chinese surname, meaning "king" or "monarch." It's used by over 100 million people worldwide.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How many Chinese surnames are there?
              </h3>
              <p className="text-gray-600">
                There are over 5,000 recorded Chinese surnames, but the top 100 surnames account for about 85% of the Chinese population.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Do Chinese surnames have meanings?
              </h3>
              <p className="text-gray-600">
                Yes, most Chinese surnames have specific meanings. For example, 李 (Lǐ) relates to plum trees, 张 (Zhāng) means "to stretch," and 刘 (Liú) refers to a type of weapon.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I choose a Chinese surname for myself?
              </h3>
              <p className="text-gray-600">
                Yes, you can choose a Chinese surname that resonates with you. Consider the meaning, pronunciation, and cultural significance when selecting a surname.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Where can I explore Chinese last names and meanings?
              </h3>
              <p className="text-gray-600">
                Scroll through our Chinese last names and meanings section to compare origins, meanings, and regional popularity so you understand the story behind each surname.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What is the Chinese signet rings last name lee crest?
              </h3>
              <p className="text-gray-600 mb-3">
                It is a symbolic seal that pairs the Lee surname with plum blossom motifs. The crest honors resilience and makes a timeless signet ring for Lee family heritage.
              </p>
              <p className="text-gray-600">
                Learn more about <RouterLink to="/blog/chinese-signet-rings" className="text-purple-600 hover:underline font-medium">Chinese signet rings last name lee crest</RouterLink> in our comprehensive guide covering family heritage, symbolism, and traditional seal designs.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How do ancient Chinese names influence modern surnames?
              </h3>
              <p className="text-gray-600">
                Ancient Chinese names introduced by kings, states, and clans shaped surnames we still use today. Understanding ancient Chinese names reveals why some families prioritized virtue or geography.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
